import { useTranslations, useLocale } from 'next-intl'
import { useRouter, usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { useAuthStore } from '@/src/shared/lib/store/auth-store'
import { useRole } from '@/src/shared/lib/hooks/use-role'
import { useModal } from '@/src/shared/hooks/useModal'
import { UserRole } from '@hummii/shared-types'

interface NavigationItem {
  label: string
  href?: string
  scrollTo?: string
  onClick?: () => void
}

export function useNavigation() {
  const t = useTranslations('header')
  const tOrders = useTranslations('clientOrders')
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const { isAuthenticated, user, setActiveRole } = useAuthStore()
  const { isActiveContractor } = useRole()
  const { open: openLoginModal } = useModal('login')
  const { open: openPostTaskModal } = useModal('post-task')

  const handlePostTask = () => {
    if (!isAuthenticated) {
      openLoginModal({
        intent: {
          intendedRole: UserRole.CLIENT,
          onSuccessAction: () => openPostTaskModal(),
        }
      })
      return
    }

    if (isActiveContractor) {
      toast.warning(tOrders('clientModeRequired'), { duration: 5000 })
      return
    }

    openPostTaskModal()
  }

  const handleBecomeContractor = () => {
    if (isAuthenticated) {
      if (user?.roles.includes(UserRole.CONTRACTOR)) {
        setActiveRole(UserRole.CONTRACTOR)
        router.push(`/${locale}/contractor/dashboard`)
      } else {
        openLoginModal({
          intent: {
            intendedRole: UserRole.CONTRACTOR,
            redirectAfterAuth: `/${locale}/contractor/onboarding`,
          }
        })
      }
    } else {
      openLoginModal({
        intent: {
          intendedRole: UserRole.CONTRACTOR,
          redirectAfterAuth: `/${locale}/contractor/onboarding`,
        }
      })
    }
  }

  const getNavigationItems = (): NavigationItem[] => {
    // Check if user is on categories page
    const isOnCategoriesPage = pathname?.includes('/categories')

    // Determine the first navigation item label and href
    const firstNavItem = isOnCategoriesPage
      ? { label: t('nav.mainPage'), href: `/${locale}` }
      : { label: t('nav.categories'), href: `/${locale}/categories` }

    if (!isAuthenticated) {
      return [
        firstNavItem,
        { label: t('nav.howItWorks'), href: `/${locale}/how-it-works` },
        { label: t('nav.postTask'), onClick: handlePostTask },
        { label: t('nav.becomeContractor'), onClick: handleBecomeContractor }
      ]
    }

    if (isActiveContractor) {
      return [
        firstNavItem,
        { label: t('nav.howItWorks'), href: `/${locale}/how-it-works` }
      ]
    }

    return [
      firstNavItem,
      { label: t('nav.howItWorks'), href: `/${locale}/how-it-works` },
      { label: t('nav.postTask'), onClick: handlePostTask }
    ]
  }

  return {
    navItems: getNavigationItems(),
    handlePostTask,
    handleBecomeContractor
  }
}
